import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { ArrowLeft, Send, Loader2 } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "hardware", label: "Hardware (laptop, monitor, printer)" },
  { value: "software", label: "Software / Application" },
  { value: "network", label: "Network / Wi-Fi / VPN" },
  { value: "account", label: "Account / Access" },
  { value: "other", label: "Other" },
];

const PRIORITIES = [
  { value: "low", label: "Low", desc: "Inconvenient, can wait" },
  { value: "medium", label: "Medium", desc: "Affects my work" },
  { value: "high", label: "High", desc: "Blocking my work" },
  { value: "urgent", label: "Urgent", desc: "Critical / many people affected" },
];

export default function NewTicketPage() {
  const nav = useNavigate();
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: "other",
    priority: "medium",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/tickets", form);
      toast.success("Thank you for reaching out. We have received your complaint and a resolution specialist is reviewing your case now. You will receive an update within the hour.", { duration: 8000 });
      nav(`/app/tickets/${data.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create ticket");
    } finally {
      setLoading(false);
    }
  };

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="p-8 lg:p-12 max-w-3xl" data-testid="new-ticket-page">
      <Link
        to="/app/tickets"
        className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-zinc-900 mb-6 transition"
        data-testid="back-to-tickets"
      >
        <ArrowLeft className="w-4 h-4" /> Back to tickets
      </Link>

      <div className="mb-10 fade-up">
        <div className="text-xs uppercase tracking-[0.3em] font-bold text-blue-600 mb-3">New ticket</div>
        <h1 className="font-display font-black tracking-tighter text-4xl text-zinc-900">Describe your issue</h1>
        <p className="text-zinc-500 mt-3">Be specific — it helps our team resolve it faster.</p>
      </div>

      <form onSubmit={submit} className="space-y-6 bg-white p-8 rounded-3xl border border-zinc-200" data-testid="ticket-form">
        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Title</label>
          <input
            required
            value={form.title}
            onChange={onChange("title")}
            placeholder="e.g. Outlook won't open on my MacBook"
            className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none"
            data-testid="ticket-title"
          />
        </div>

        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Description</label>
          <textarea
            required
            value={form.description}
            onChange={onChange("description")}
            rows={6}
            placeholder="What happened? What have you tried? Include any error messages or screenshots."
            className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none resize-none"
            data-testid="ticket-description"
          />
        </div>

        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-zinc-500">Category</label>
          <select
            value={form.category}
            onChange={onChange("category")}
            className="mt-2 w-full rounded-xl border-2 border-zinc-200 bg-white px-4 py-3 text-sm focus:border-blue-600 focus:outline-none"
            data-testid="ticket-category"
          >
            {CATEGORIES.map((c) => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="text-xs font-bold tracking-widest uppercase text-zinc-500 mb-3 block">Priority</label>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            {PRIORITIES.map((p) => (
              <button
                key={p.value}
                type="button"
                onClick={() => setForm({ ...form, priority: p.value })}
                className={`p-3 rounded-xl border-2 text-left transition ${
                  form.priority === p.value
                    ? "border-zinc-900 bg-zinc-900 text-white"
                    : "border-zinc-200 bg-white hover:border-zinc-400"
                }`}
                data-testid={`priority-${p.value}`}
              >
                <div className="font-bold text-sm">{p.label}</div>
                <div className={`text-[10px] mt-1 ${form.priority === p.value ? "text-zinc-300" : "text-zinc-500"}`}>{p.desc}</div>
              </button>
            ))}
          </div>
        </div>

        <button
          type="submit"
          disabled={loading}
          className="w-full inline-flex items-center justify-center gap-2 bg-zinc-900 text-white font-bold py-4 rounded-full hover:bg-zinc-800 disabled:opacity-50 transition-all hover:-translate-y-0.5"
          data-testid="ticket-submit"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Submit ticket <Send className="w-4 h-4" /></>}
        </button>
      </form>
    </div>
  );
}
